import type { Vec2, PolygonWithHoles, AABB, Segment, OBB } from './types';
import { pointInPolygon, ringBounds, obbCorners } from './polygon';
import { createSpatialHash } from './spatialHash';
import { obbSegmentOverlap } from './sat';

export interface PolygonIndex {
  contains(p: Vec2): boolean;
  edgesNear(box: AABB): readonly Segment[];
  nearestBoundaryPoint(p: Vec2, maxDistance: number): Vec2 | null;
  obbInside(o: OBB): boolean;
}

interface IndexedSegment {
  readonly segment: Segment;
  readonly polyIndex: number;
}

export function createPolygonIndex(
  polys: readonly PolygonWithHoles[],
  cellSize: number
): PolygonIndex {
  const segmentHash = createSpatialHash<IndexedSegment>(cellSize);
  const polyBounds: AABB[] = [];

  for (let pi = 0; pi < polys.length; pi++) {
    const poly = polys[pi]!;
    polyBounds.push(ringBounds(poly.outer));

    const rings = [poly.outer, ...poly.holes];
    for (const ring of rings) {
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i]!;
        const b = ring[(i + 1) % ring.length]!;
        const segment: Segment = { a, b };
        const box: AABB = {
          minX: Math.min(a[0], b[0]),
          minY: Math.min(a[1], b[1]),
          maxX: Math.max(a[0], b[0]),
          maxY: Math.max(a[1], b[1]),
        };
        segmentHash.insert({ segment, polyIndex: pi }, box);
      }
    }
  }

  function contains(p: Vec2): boolean {
    for (let i = 0; i < polys.length; i++) {
      const bounds = polyBounds[i]!;
      if (p[0] < bounds.minX || p[0] > bounds.maxX || p[1] < bounds.minY || p[1] > bounds.maxY) {
        continue;
      }
      if (pointInPolygon(p, polys[i]!)) {
        return true;
      }
    }
    return false;
  }

  function edgesNear(box: AABB): readonly Segment[] {
    const indexed = segmentHash.query(box);
    return indexed.map((s) => s.segment);
  }

  function nearestBoundaryPoint(p: Vec2, maxDistance: number): Vec2 | null {
    const queryBox: AABB = {
      minX: p[0] - maxDistance,
      minY: p[1] - maxDistance,
      maxX: p[0] + maxDistance,
      maxY: p[1] + maxDistance,
    };

    const segments = segmentHash.query(queryBox);
    let bestDist = Infinity;
    let bestPoint: Vec2 | null = null;

    for (const { segment } of segments) {
      const closest = closestPointOnSegment(p, segment);
      const dx = closest[0] - p[0];
      const dy = closest[1] - p[1];
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < bestDist && dist <= maxDistance) {
        bestDist = dist;
        bestPoint = closest;
      }
    }

    return bestPoint;
  }

  function obbInside(o: OBB): boolean {
    const corners = obbCorners(o);

    for (const corner of corners) {
      if (!contains(corner)) {
        return false;
      }
    }

    const obbBox: AABB = {
      minX: Math.min(corners[0][0], corners[1][0], corners[2][0], corners[3][0]),
      minY: Math.min(corners[0][1], corners[1][1], corners[2][1], corners[3][1]),
      maxX: Math.max(corners[0][0], corners[1][0], corners[2][0], corners[3][0]),
      maxY: Math.max(corners[0][1], corners[1][1], corners[2][1], corners[3][1]),
    };

    const nearSegments = segmentHash.query(obbBox);
    for (const { segment } of nearSegments) {
      if (obbSegmentOverlap(o, segment)) {
        return false;
      }
    }

    return true;
  }

  return { contains, edgesNear, nearestBoundaryPoint, obbInside };
}

function closestPointOnSegment(p: Vec2, s: Segment): Vec2 {
  const { a, b } = s;
  const ab: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const ap: Vec2 = [p[0] - a[0], p[1] - a[1]];
  const lenSq = ab[0] * ab[0] + ab[1] * ab[1];

  if (lenSq < 1e-10) {
    return a;
  }

  const t = Math.max(0, Math.min(1, (ap[0] * ab[0] + ap[1] * ab[1]) / lenSq));
  return [a[0] + t * ab[0], a[1] + t * ab[1]];
}
